/**
 * Remembers the page a signed-out user tried to open (e.g. a deeplink from a
 * mail), so login can send them there instead of to the start page.
 *
 * Kept in sessionStorage rather than a `?redirect=` query: the SSO login leaves
 * the SPA (Entree -> /oidc/redirect -> /login?code=…) and the school choice
 * reloads the page twice, which both drop the query. sessionStorage survives
 * that within the tab the mail link opened.
 */
const INTENDED_PATH_KEY = "intended_path";

// Pages of the login flow itself: returning to one of these after login is
// meaningless (or loops), so they are never remembered.
const AUTH_PATHS = [
  "/login",
  "/login-by-email",
  "/inloggen",
  "/2fa",
  "/school-login-choice",
  "/forgot-password",
  "/reset-password",
];

/**
 * Only same-origin app paths qualify: "//host" is protocol-relative and would
 * turn this into an open redirect.
 */
const isRememberablePath = (path: string): boolean => {
  if (path.startsWith("/") === false || path.startsWith("//") === true) {
    return false;
  }

  const pathname = path.split(/[?#]/)[0];

  return AUTH_PATHS.includes(pathname) === false;
};

export const rememberIntendedPath = (path: string): void => {
  if (isRememberablePath(path) === false) {
    return;
  }

  sessionStorage.setItem(INTENDED_PATH_KEY, path);
};

/**
 * Returns the remembered path once and forgets it, so a later login in the
 * same tab starts on the start page again.
 */
export const consumeIntendedPath = (): string | null => {
  const path = sessionStorage.getItem(INTENDED_PATH_KEY);
  sessionStorage.removeItem(INTENDED_PATH_KEY);

  if (path === null || isRememberablePath(path) === false) {
    return null;
  }

  return path;
};

/**
 * Drops the remembered path on logout, so the next session in this tab starts
 * on the start page (e.g. another student on a shared computer).
 */
export const forgetIntendedPath = (): void => {
  sessionStorage.removeItem(INTENDED_PATH_KEY);
};
